import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Star, EyeOff, Scale } from 'lucide-react';
import { toast } from 'sonner';

const ReviewModal = ({ open, onOpenChange, targetUser, context, contextId, onSuccess }) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Services Marketplace reviews are blind (hidden until both submit or 7 days pass).
  const isProListing = context === 'pro_listing';

  const handleSubmit = async () => {
    if (rating < 1 || rating > 5) {
      toast.error(t('reviews.pleaseSelectRating'));
      return;
    }

    setSubmitting(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error(t('reviews.pleaseLogin'));
        return;
      }

      await api.post(
        '/api/reviews',
        {
          to_user_id: targetUser.id,
          rating,
          comment,
          context,
          context_id: contextId
        },
        {
          headers: {
            Authorization: `Bearer ${session.session.access_token}`
          }
        }
      );

      toast.success(t('reviews.reviewSubmitted'));
      setRating(5);
      setComment('');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Review error:', error);
      toast.error(t('reviews.failedToSubmit'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="review-modal">
        <DialogHeader>
          <DialogTitle>{t('reviews.leaveReview')}</DialogTitle>
          <DialogDescription>
            {t('reviews.rateExperience')} {targetUser?.name || 'this user'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {isProListing && (
            <>
              <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                <EyeOff size={16} className="mt-0.5 shrink-0 text-blue-600" aria-hidden="true" />
                <span>{t('reviews.blindNotice')}</span>
              </div>
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <Scale size={16} className="mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
                <span>{t('reviews.factualReminder')}</span>
              </div>
            </>
          )}
          <div className="space-y-2">
            <Label>{t('reviews.rating')} *</Label>
            <div className="flex gap-1" role="group" aria-label={t('reviews.rating')}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  aria-label={t('reviews.rateStars', { count: star })}
                  aria-pressed={star === rating}
                  className="p-1.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-transform hover:scale-110"
                >
                  <Star
                    size={32}
                    className={star <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
                    aria-hidden="true"
                  />
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="review-comment">{t('reviews.commentOptional')}</Label>
            <Textarea
              id="review-comment"
              placeholder={t('reviews.shareExperience')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              className="rounded-xl"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            {t('verification.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? t('reviews.submitting') : t('reviews.submitReview')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewModal;
